import { supabase } from "@/integrations/supabase/client";

const avatarUrlCache = new Map<string, string>();

/**
 * Resolve a stored avatar path (private "avatars" bucket) to a temporary signed URL.
 * Returns null when path is empty or signing fails.
 */
export async function resolveAvatarUrl(path: string | null | undefined): Promise<string | null> {
  if (!path) return null;
  // If the value is already a full URL (legacy), return as-is.
  if (/^https?:\/\//i.test(path)) return path;
  const cached = avatarUrlCache.get(path);
  if (cached) return cached;
  const { data, error } = await supabase.storage
    .from("avatars")
    .createSignedUrl(path, 60 * 60 * 24 * 7); // 7 days
  if (error || !data) return null;
  avatarUrlCache.set(path, data.signedUrl);
  return data.signedUrl;
}

/**
 * Upload an avatar image to the "avatars" bucket under members/{userId}/...
 * Returns the storage path on success.
 */
export async function uploadAvatar(userId: string, file: File): Promise<string> {
  const ext = (file.name.split(".").pop() || "jpg").toLowerCase().replace(/[^a-z0-9]/g, "");
  const path = `members/${userId}/${Date.now()}.${ext || "jpg"}`;
  const { error } = await supabase.storage
    .from("avatars")
    .upload(path, file, { upsert: true, contentType: file.type || "image/jpeg" });
  if (error) throw new Error(error.message);
  return path;
}
